import React, { useState, useEffect, useMemo } from 'react';
import { 
    useGroupWorkStore, 
    IOrder, 
    IOrderDetail, 
    IGroupWorkAssignment,
    GroupMember,
    IDailyProgress, 
    IAdminNote,
} from '../store/useGroupWorkStore'; 

import { UserGroupIcon, ClipboardDocumentListIcon, ClockIcon, CheckCircleIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import VoiceNoteRecorder from '../component/VoiceRecorder';

// =================================================================
// SUB-COMPONENT 1: MemberSelector (UI Change)
// =================================================================
interface MemberSelectorProps {
    members: GroupMember[];
    selectedMember: GroupMember | null;
    onSelectMember: (member: GroupMember) => void;
}

const MemberSelector: React.FC<MemberSelectorProps> = ({ members, selectedMember, onSelectMember }) => (
    <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
        <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                <UserGroupIcon className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Step 1: Select Your Name</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {members.map(member => (
                <button
                    key={member._id}
                    onClick={() => onSelectMember(member)}
                    className={`relative py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        selectedMember?._id === member._id
                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-200 scale-[1.02]'
                            : 'bg-gray-100 border border-gray-400 hover:border-indigo-300 hover:bg-indigo-50/50 text-gray-700 hover:text-indigo-700'
                    // } ${

                    //     member.activeProgressId && selectedMember?._id !== member._id ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
  
                >
                    {member.name}
                    
                    {/* {member.activeProgressId && (
                         <span className="absolute -top-2 -right-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                            In Progress
                         </span>
                    )} */}
                </button>
            ))}
        </div>
    </div>
);


interface AdminNotesSectionProps {
    adminNotes: IAdminNote[];
}

const AdminNotesSection: React.FC<AdminNotesSectionProps> = ({ adminNotes }) => {
    // Sabhi orders se saare notes ko ek flat array mein nikal lein
    const allFlattenedNotes = useMemo(() => {
        return adminNotes.flatMap(noteGroup => 
            noteGroup.notes.map(note => ({
                ...note,
                adminName: noteGroup.userId.fullName || 'Admin'
            }))
        ).sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()); // Sabse naya upar
    }, [adminNotes]);

    if (allFlattenedNotes.length === 0) {
        return null; // Agar koi note nahi hai toh kuch na dikhayein
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
            <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-blue-500 to-sky-600 rounded-lg">
                        <ChatBubbleBottomCenterTextIcon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Admin Voice Notes</h2>
                </div>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                {allFlattenedNotes.map((note) => (
                    <div key={note._id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                            From: {note.adminName}
                        </p>
                        <VoiceNoteRecorder
                            existingNotes={[note]}
                            isReadOnly={true}
                            onUpdate={() => {}} // Read-only hai, isliye khaali function
                        />
                        <p className="text-xs text-gray-500 mt-1.5 text-right">
                            {format(new Date(note.uploadedAt), "dd MMM yyyy, h:mm a")}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};


// =================================================================
// SUB-COMPONENT 2: AssignedTaskCard (UI Change)
// =================================================================
interface AssignedTaskCardProps {
    orderLotNumber: string;
    detail: IOrderDetail;
    assignment: IGroupWorkAssignment;
    selectedMember: GroupMember | null;
    onClaim: (orderId: string, assignmentId: string, pieces: number) => void;
  
    // isMemberBusy: boolean;
}

const AssignedTaskCard: React.FC<AssignedTaskCardProps> = ({ orderLotNumber, detail, assignment, selectedMember, onClaim }) => {
    const [piecesToClaim, setPiecesToClaim] = useState('');

    const totalClaimed = useMemo(() => assignment.dailyProgress.reduce((sum, p) => sum + p.piecesClaimed, 0), [assignment.dailyProgress]);
    const remainingPieces = detail.totalPieces - totalClaimed;
    const progressPercent = detail.totalPieces > 0 ? (totalClaimed / detail.totalPieces) * 100 : 0;

    const handleClaimClick = () => {
        if (!selectedMember) {
            toast.error("Please select your name from Step 1 first!");
            return;
        }
        const pieces = parseInt(piecesToClaim);
        if (isNaN(pieces) || pieces <= 0) {
            toast.error(`Please enter a positive number.`);
            return;
        }
        if (pieces > remainingPieces) {
             toast.error(`You cannot claim more than the remaining ${remainingPieces} pieces.`);
             return;
        }
        onClaim(detail._id, assignment._id, pieces); 
        setPiecesToClaim('');
    };

    const isCompleted = remainingPieces <= 0;
    
    
    // const canClaim = selectedMember && !isMemberBusy && remainingPieces > 0;
    const canClaim = selectedMember && remainingPieces > 0;

    return (
        <div className={`relative overflow-hidden bg-white rounded-xl border transition-all duration-300 ${
            isCompleted 
                ? 'border-emerald-200 bg-gradient-to-br from-emerald-50/30 to-white' 
                : (canClaim ? 'hover:shadow-lg hover:border-indigo-200' : 'opacity-60 bg-gray-50') // Opacity change agar claim nahi kar sakte
        }`}>
            {isCompleted && (
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-500 opacity-10 rounded-bl-full"></div>
            )}
            
            <div className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                    <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase">LOT: {orderLotNumber}</p>
                        <h3 className="text-xl font-bold text-gray-900">{detail.sizeSegmentId.segmentName}</h3>
                    </div>
                    <span className={`px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm ${
                        isCompleted 
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                            : 'bg-blue-100 text-blue-700 border border-blue-200'
                    }`}>
                        {isCompleted ? '✓ Completed' : 'In Progress'}
                    </span>
                </div>
                
                <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                        <span className="font-medium text-gray-600">Progress</span>
                        <span className="font-bold text-gray-900">
                            <span className={isCompleted ? 'text-emerald-600' : 'text-indigo-600'}>{totalClaimed}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className="text-gray-600">{detail.totalPieces}</span>
                            <span className="text-gray-500 text-xs ml-1">Claimed</span>
                        </span>
                    </div>
                    <div className="relative w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <div 
                            className={`h-full rounded-full transition-all duration-700 ease-out ${
                                isCompleted 
                                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' 
                                    : 'bg-gradient-to-r from-indigo-500 to-purple-600'
                            }`} 
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                    </div>
                </div>

                {!isCompleted && (
                    <div className="pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="flex-1 relative">
                                <input 
                                    type="number" 
    
                                    // placeholder={isMemberBusy ? "Complete active task first" : `Max ${remainingPieces}`} 
                                    placeholder={`Max ${remainingPieces}`}
                                    value={piecesToClaim} 
                                    onChange={(e) => setPiecesToClaim(e.target.value)} 
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400 transition-all" 
       
                                    disabled={!canClaim} 
                                />
                            </div>
                            <button 
                                onClick={handleClaimClick} 
                                className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 shadow-md hover:shadow-lg disabled:shadow-none" 
                                
                                disabled={!canClaim || !piecesToClaim}
                            >
                                Claim
                            </button>
                        </div>
                    </div>
                )}
                
                {isCompleted && (
                    <div className="pt-4 border-t border-emerald-100">
                        <div className="flex items-center justify-center gap-2 py-2">
                            <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                            <p className="font-semibold text-emerald-700 text-sm">All pieces completed!</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// =================================================================
// SUB-COMPONENT 3: DailyProgressLog (UI Change)
// =================================================================
interface FlattenedAssignment {
    order: IOrder;
    detail: IOrderDetail;
    assignment: IGroupWorkAssignment;
}

interface DailyProgressLogProps {
    assignments: FlattenedAssignment[];
    onComplete: (progressId: string) => void;
    // Edit action ke liye
    onUpdateClaim: (progressId: string, newPieces: number) => void;
    //  Yeh check karne ke liye ki member ka active task kaunsa hai

    // activeProgressId: string | null;

    // Selected member ko pass karein
    selectedMember: GroupMember | null;
}


// Yeh row ko edit-in-place banayega
const DailyProgressRow: React.FC<{
    progress: IDailyProgress;
    detail: IOrderDetail;
    onComplete: (progressId: string) => void;
    onUpdateClaim: (progressId: string, newPieces: number) => void;
    // isMyActiveTask: boolean;
    // isMemberBusy: boolean;
    selectedMember: GroupMember | null;
}> = ({ progress, detail, onComplete, onUpdateClaim,
    //  isMyActiveTask,
    //   isMemberBusy, 
      selectedMember }) => {
    
    const [currentPieces, setCurrentPieces] = useState(progress.piecesClaimed.toString());
    const [isSaving, setIsSaving] = useState(false);

    // Jab store se data refresh ho, toh local state ko bhi update karein
    useEffect(() => {
        setCurrentPieces(progress.piecesClaimed.toString());
    }, [progress.piecesClaimed]);

    const hasChanged = progress.piecesClaimed.toString() !== currentPieces;
    
   
    const isMyTask = selectedMember?._id === progress.memberId;
    const isDisabled = progress.status === 'Completed' || !selectedMember || !isMyTask;

    const handleUpdate = async () => {
        const newPieces = parseInt(currentPieces, 10);
        if (isNaN(newPieces) || newPieces <= 0) {
            toast.error("Please enter a valid number greater than 0.");
            setCurrentPieces(progress.piecesClaimed.toString()); // Reset to original
            return;
        }
        
        setIsSaving(true);
        await onUpdateClaim(progress._id, newPieces);
        // Store se data refresh hone ke baad, 'useEffect' upar local state update kar dega
        // aur 'isSaving' ko false set kar denge (kyunki component re-render hoga)
        setIsSaving(false);
    };

    return (
        <tr className={`hover:bg-gray-50/50 transition-colors duration-150 ${!isMyTask && progress.status === 'In Progress' ? 'opacity-50 bg-gray-50' : ''}`}>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm font-semibold text-gray-900">{detail.sizeSegmentId.segmentName}</span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className={`text-sm ${isMyTask ? 'font-bold text-indigo-700' : 'text-gray-700'}`}>{progress.memberName}</span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <input
                    type="number"
                    value={currentPieces}
                    onChange={(e) => setCurrentPieces(e.target.value)}
                    
                    disabled={isDisabled || isSaving}
                    className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-indigo-700 focus:ring-2 focus:ring-indigo-400 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                />
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border ${
                    progress.status === 'Completed' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                    {progress.status === 'Completed' && '✓ '}
                    {progress.status}
                </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-center">
                {progress.status === 'In Progress' && (
                    <div className="flex justify-center gap-2">
                        <button 
                            onClick={handleUpdate} 
                            
                            disabled={!hasChanged || isDisabled || isSaving}
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 active:scale-95 shadow-sm hover:shadow-md disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed"
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </button>
                        <button 
                            onClick={() => onComplete(progress._id)} 
                            
                            disabled={hasChanged || isDisabled || isSaving}
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white text-xs font-semibold rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 active:scale-95 shadow-sm hover:shadow-md disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed"
                        >
                            Mark Complete
                        </button>
                    </div>
                )}
            </td>
        </tr>
    );
}

const DailyProgressLog: React.FC<DailyProgressLogProps> = ({ assignments, onComplete, onUpdateClaim,  selectedMember }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
        <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                    <ClockIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Step 3: Track and Complete Your Work</h2>
            </div>
            {!selectedMember && (
                <div className="p-4 bg-yellow-50 border-b border-yellow-200 text-yellow-800 text-sm font-medium text-center">
                    Please select your name in Step 1 to edit your tasks.
                </div>
            )}
        </div>
        
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Size</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Member</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Pieces Claimed</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {assignments.flatMap(a =>
                        a.assignment.dailyProgress.map(p => (
                            <DailyProgressRow
                                key={p._id}
                                detail={a.detail} // detail ko pass karein
                                progress={p}
                                onComplete={onComplete}
                                onUpdateClaim={onUpdateClaim}
                                // isMyActiveTask={p._id === activeProgressId}
                                // isMyActiveTask={false}
                                // isMemberBusy={!!activeProgressId}
                                selectedMember={selectedMember} 
                            />
                        ))
                    )}
                </tbody>
            </table>
        </div>
    </div>
);


// =================================================================
// MAIN COMPONENT: GroupWorkDashboard
// =================================================================
const GroupWorkDashboard: React.FC = () => {
    // ✅ NAYA ACTION: updateClaimedPieces ko store se lein
    const { orders, groupMembers, isLoading, fetchTasks, claimPieces, markTaskComplete, updateClaimedPieces } = useGroupWorkStore();
    const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    // // ✅ NAYA LOGIC: Check karein ki selected member busy hai ya nahi
    // const isSelectedMemberBusy = useMemo(() => {
    //     // activeProgressId ke liye groupMembers list se fresh data lein
    //     const currentMember = groupMembers.find(m => m._id === selectedMember?._id);
    //     return !!currentMember?.activeProgressId;
    // }, [selectedMember, groupMembers]);

    // // ✅ NAYA LOGIC: Active task ka ID store karein
    // const activeProgressId = useMemo(() => {
    //     const currentMember = groupMembers.find(m => m._id === selectedMember?._id);
    //     return currentMember?.activeProgressId || null;
    // }, [selectedMember, groupMembers]);

    const allAssignments: FlattenedAssignment[] = useMemo(() => 
        orders.flatMap(order =>
            (order.orderDetails || []).flatMap((detail: IOrderDetail) =>
                (detail.groupWorkAssignments || []).map((assignment: IGroupWorkAssignment) => ({ order, detail, assignment }))
            )
        ), [orders]);

    const allAdminNotes = useMemo(() => {
        return orders.flatMap(order => order.adminNotes || []);
    }, [orders]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center space-y-4">
                    <div className="relative w-16 h-16 mx-auto">
                        <div className="absolute top-0 left-0 w-full h-full border-4 border-indigo-200 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-full h-full border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                    <p className="text-lg font-semibold text-gray-700">Loading Your Dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 tracking-tight mb-2">Group Work Dashboard</h1>
                    <p className="text-gray-600">Track your assignments, claim pieces, and complete tasks efficiently</p>
                </div>
                
                {/* Step 1: Member Selection */}
                {groupMembers && groupMembers.length > 0 ? (
                    <MemberSelector members={groupMembers} selectedMember={selectedMember} onSelectMember={setSelectedMember} />
                ) : (
                    !isLoading && (
                        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                <UserGroupIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-600 font-medium">No group members are configured for your profile.</p>
                        </div>
                    )
                )}

                {/* Step 2 & 3 */}
                {orders.length > 0 ? (
                    <>
                        {/* Step 2: Claim Pieces */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
                            <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                                        <ClipboardDocumentListIcon className="w-6 h-6 text-white" />
                                    </div>
                                    <h2 className="text-lg font-semibold text-gray-900">Step 2: Claim Pieces from Your Assigned Tasks</h2>
                                </div>
                                {/* ✅ NAYA UI: Busy message dikhayein */}
                                {/* {isSelectedMemberBusy && (
                                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm font-medium">
                                        You have an active task in progress. Please complete it before claiming new pieces.
                                    </div>
                                )} */}
                            </div>
                            
                            <div className="p-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                    {allAssignments.map(({ order, detail, assignment }) => (
                                        <AssignedTaskCard
                                            key={assignment._id}
                                            orderLotNumber={order.lotNumber}
                                            detail={detail}
                                            assignment={assignment}
                                            selectedMember={selectedMember}
                                            // ✅ NAYA PROP: Pass karein ki member busy hai ya nahi
                                            // isMemberBusy={isSelectedMemberBusy}
                                            onClaim={(_detailId, assignmentId, pieces) => {
                                                if (selectedMember) {
                                                    claimPieces(order._id, assignmentId, selectedMember._id, selectedMember.name, pieces);
                                                } else {
                                                    toast.error("Please select your name first!");
                                                }
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Step 3: Progress Log */}
                        <DailyProgressLog 
                            assignments={allAssignments} 
                            onComplete={markTaskComplete}
                            // ✅ NAYE PROPS: Actions pass karein
                            onUpdateClaim={updateClaimedPieces}
                            // activeProgressId={activeProgressId}
                            selectedMember={selectedMember}
                        />
                    </>
                ) : (
                    <div className="flex items-center justify-center py-20">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center max-w-md">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-6">
                                <CheckCircleIcon className="w-10 h-10 text-indigo-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">You're All Caught Up!</h2>
                            <p className="text-gray-600">No work has been assigned to your group yet. Check back later for new tasks.</p>
                        </div>
                    </div>
                )}
            </div>
            <AdminNotesSection adminNotes={allAdminNotes} />
        </div>
    );
};

export default GroupWorkDashboard;

